import { requestLaravel } from "../../../../../../utils/api";
import type { StakeholderApi } from "../../common/StakeholderEngagement/types";
import { ApiResponse, IProjectDocument } from "../../../../core/_models";
import { IFinalFeasibilityValidation, IESGFinalCheck, ICostBenefitFinalModel, IFiscalSustainabilityIfsa, IDecisionData, IInfrastructureMasterPlan, ILongTermAssetManagement, IEconomicAndCommercialViability, IImplementationStrategy, IRiskStatement, IEconomicMultiplierAndMacroeconomicsBenefits, ISupervisionReportingAndEvaluation, IDigitalTechnologyLeverage, IInstitutionalAndOperationalReadiness, IProjectCommunicationAndStakeholderEngagementPlan, IFinacialRequirmentsAndFundingstrategy, IProjectPhase, IProjectPhaseMilestone, IStakeHolder, IKeyVariablesSensitivity, ISummaryOfOptionConsidered, IOptionsConsidered } from "./_models";

function updateprojectstage4minieditor<TResponse, TPayload extends { project_id: number }>(
    updateData: TPayload,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<TResponse>>(
        "/project-stage-four/mini-editor/update",
        updateData,
        { signal }
    );
}

function getprojectstage4minieditor<TResponse, TPayload extends { project_id: number }>(
    payload: TPayload,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<TResponse>>(
        "/project-stage-four/mini-editor/get",
        payload,
        { signal }
    );
}

interface IFinalFeasibilityValidationReponse extends IFinalFeasibilityValidation {
    analysis_mode_selector: 'cba' | 'cea';
    total_economic_benefit: number;
    total_project_cost: number;
    total_outcome_units_delivered: number;
    fiscal_affordability_index: number;
}

export function getFinalFeasibilityValidation(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFinalFeasibilityValidationReponse>>(
        "/project-stage-four/final-feasibility-validation/get",
        { project_id },
        { signal }
    );
}

interface UpdateFinalFeasibilityValidationPayload extends IFinalFeasibilityValidation {
    project_id: number;
}

export function updateFinalFeasibilityValidation(updateData: UpdateFinalFeasibilityValidationPayload) {
    return requestLaravel<ApiResponse<IFinalFeasibilityValidation>>(
        "/project-stage-four/final-feasibility-validation/update",
        updateData
    );
}

interface UpdateCostBenefitFinalModelPayload extends ICostBenefitFinalModel {
    project_id: number;
}

export function updateCostBenefitFinalModel(updateData: UpdateCostBenefitFinalModelPayload) {
    return requestLaravel<ApiResponse<ICostBenefitFinalModel>>(
        "/project-stage-four/cba-cea-final-model/update",
        updateData
    );
}

export function getCostBenefitFinalModel(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<ICostBenefitFinalModel>>(
        "/project-stage-four/cba-cea-final-model/get",
        { project_id },
        { signal }
    );
}

export function getLongTermAssetManagement(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<ILongTermAssetManagement>>(
        "/project-stage-four/lifecycle-asset-management/get",
        { project_id },
        { signal }
    );
}

interface UpdateLifecycleAssetSustainabilityPayload extends ILongTermAssetManagement {
    project_id: number;
}

export function updateLongTermAssetManagement(updateData: UpdateLifecycleAssetSustainabilityPayload) {
    return requestLaravel<ApiResponse<ILongTermAssetManagement>>(
        "/project-stage-four/lifecycle-asset-management/update",
        updateData
    );
}

export function getESGFinalCheck(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IESGFinalCheck>>(
        "/project-stage-four/social-environmental-gender-impact/get",
        { project_id },
        { signal }
    );
}

interface UpdateESGFinalCheckPayload extends IESGFinalCheck {
    project_id: number;
}

export function updateESGFinalCheck(updateData: UpdateESGFinalCheckPayload) {
    return requestLaravel<ApiResponse<IESGFinalCheck>>(
        "/project-stage-four/social-environmental-gender-impact/update",
        updateData
    );
}

interface IFiscalSustainabilityIfsaResponse extends IFiscalSustainabilityIfsa {
    analysis_mode_selector: 'cba' | 'cea';
    total_project_cost: number;
    total_economic_benefit: number;
}

export function fetchFiscalSustainabilityIfsaData(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFiscalSustainabilityIfsaResponse>>(
        "/project-stage-four/fiscal-sustainability-ifsa/get",
        { project_id },
        { signal }
    );
}

export function updateFiscalSustainabilityIfsaData(updateData: IFiscalSustainabilityIfsa & { project_id: number }) {
    return requestLaravel<ApiResponse<IFiscalSustainabilityIfsa>>(
        "/project-stage-four/fiscal-sustainability-ifsa/update",
        updateData
    );
}

export function saveProjectStageFour(payload: { project_id: string; dk_stage: string }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<void>>(
        "/project-stage-four/stage/submit",
        { project_id: payload.project_id },
        { signal }
    );
}

export function getFinalInvestmentDecisionEngine(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IDecisionData | null>>(
        "/project-stage-four/final-investment-decision/get",
        payload,
        { signal }
    );
}

export function getInfrastructureMasterPlan(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IInfrastructureMasterPlan>>(
        "/project-stage-four/infrastructure-master-plan/get",
        { project_id },
        { signal }
    );
}

export function updateInfrastructureMasterPlan(updateData: IInfrastructureMasterPlan & { project_id: number }) {
    return requestLaravel<ApiResponse<IInfrastructureMasterPlan>>(
        "/project-stage-four/infrastructure-master-plan/update",
        updateData
    );
}

export interface updateEconomicAndCommercialViabilityPayload
    extends Omit<
        IEconomicAndCommercialViability,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateEconomicAndCommercialViability(
    updateData: updateEconomicAndCommercialViabilityPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<IEconomicAndCommercialViability, updateEconomicAndCommercialViabilityPayload>(
        updateData,
        signal
    );
}

export function getEconomicAndCommercialViability(
    payload: updateEconomicAndCommercialViabilityPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<IEconomicAndCommercialViability, updateEconomicAndCommercialViabilityPayload>(
        payload,
        signal
    );
}

export interface updateImplementationStrategyPayload
    extends Omit<
        IImplementationStrategy,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateImplementationStrategy(
    updateData: updateImplementationStrategyPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<IImplementationStrategy, updateImplementationStrategyPayload>(
        updateData,
        signal
    );
}

export function getImplementationStrategy(
    payload: updateImplementationStrategyPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<IImplementationStrategy, updateImplementationStrategyPayload>(
        payload,
        signal
    );
}

export interface updateRiskStatementPayload
    extends Omit<
        IRiskStatement,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateRiskStatement(
    updateData: updateRiskStatementPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<IRiskStatement, updateRiskStatementPayload>(
        updateData,
        signal
    );
}

export function getRiskStatement(
    payload: updateRiskStatementPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<IRiskStatement, updateRiskStatementPayload>(
        payload,
        signal
    );
}

export interface updateEconomicMultiplierAndMacroeconomicsBenefitsPayload
    extends Omit<
        IEconomicMultiplierAndMacroeconomicsBenefits,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateEconomicMultiplierAndMacroeconomicsBenefits(
    updateData: updateEconomicMultiplierAndMacroeconomicsBenefitsPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<IEconomicMultiplierAndMacroeconomicsBenefits, updateEconomicMultiplierAndMacroeconomicsBenefitsPayload>(
        updateData,
        signal
    );
}

export function getEconomicMultiplierAndMacroeconomicsBenefits(
    payload: updateEconomicMultiplierAndMacroeconomicsBenefitsPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<IEconomicMultiplierAndMacroeconomicsBenefits, updateEconomicMultiplierAndMacroeconomicsBenefitsPayload>(
        payload,
        signal
    );
}

export interface updateSupervisionReportingAndEvaluationPayload
    extends Omit<
        ISupervisionReportingAndEvaluation,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateSupervisionReportingAndEvaluation(
    updateData: updateSupervisionReportingAndEvaluationPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<ISupervisionReportingAndEvaluation, updateSupervisionReportingAndEvaluationPayload>(
        updateData,
        signal
    );
}

export function getSupervisionReportingAndEvaluation(
    payload: updateSupervisionReportingAndEvaluationPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<ISupervisionReportingAndEvaluation, updateSupervisionReportingAndEvaluationPayload>(
        payload,
        signal
    );
}

export interface updateDigitalTechnologyLeveragePayload
    extends Omit<
        IDigitalTechnologyLeverage,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateDigitalTechnologyLeverage(
    updateData: updateDigitalTechnologyLeveragePayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<IDigitalTechnologyLeverage, updateDigitalTechnologyLeveragePayload>(
        updateData,
        signal
    );
}

export function getDigitalTechnologyLeverage(
    payload: updateDigitalTechnologyLeveragePayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<IDigitalTechnologyLeverage, updateDigitalTechnologyLeveragePayload>(
        payload,
        signal
    );
}

// ============================================
// Institutional & Operational Readiness
// ============================================

export interface updateInstitutionalAndOperationalReadinessPayload
    extends Omit<
        IInstitutionalAndOperationalReadiness,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateInstitutionalAndOperationalReadiness(
    updateData: updateInstitutionalAndOperationalReadinessPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<IInstitutionalAndOperationalReadiness, updateInstitutionalAndOperationalReadinessPayload>(
        updateData,
        signal
    );
}

export function getInstitutionalAndOperationalReadiness(
    payload: updateInstitutionalAndOperationalReadinessPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<IInstitutionalAndOperationalReadiness, updateInstitutionalAndOperationalReadinessPayload>(
        payload,
        signal
    );
}

// ============================================
// Project Communication & Stakeholder Engagement Plan
// ============================================

export interface updateProjectCommunicationAndStakeholderEngagementPlanPayload
    extends Omit<
        IProjectCommunicationAndStakeholderEngagementPlan,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateProjectCommunicationAndStakeholderEngagementPlan(
    updateData: updateProjectCommunicationAndStakeholderEngagementPlanPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<IProjectCommunicationAndStakeholderEngagementPlan, updateProjectCommunicationAndStakeholderEngagementPlanPayload>(
        updateData,
        signal
    );
}

export function getProjectCommunicationAndStakeholderEngagementPlan(
    payload: updateProjectCommunicationAndStakeholderEngagementPlanPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<IProjectCommunicationAndStakeholderEngagementPlan, updateProjectCommunicationAndStakeholderEngagementPlanPayload>(
        payload,
        signal
    );
}

// ============================================
// Financial Requirements & Funding Strategy
// ============================================

export interface updateFinancialRequirementsAndFundingStrategyPayload
    extends Omit<
        IFinacialRequirmentsAndFundingstrategy,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateFinancialRequirementsAndFundingStrategy(
    updateData: updateFinancialRequirementsAndFundingStrategyPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<IFinacialRequirmentsAndFundingstrategy, updateFinancialRequirementsAndFundingStrategyPayload>(
        updateData,
        signal
    );
}

export function getFinancialRequirementsAndFundingStrategy(
    payload: updateFinancialRequirementsAndFundingStrategyPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<IFinacialRequirmentsAndFundingstrategy, updateFinancialRequirementsAndFundingStrategyPayload>(
        payload,
        signal
    );
}

export function storeOrUpdateInfrastructureMasterPlan(updateData: IInfrastructureMasterPlan & { project_id: number }) {
    return requestLaravel<ApiResponse<IInfrastructureMasterPlan>>(
        "/project-stage-four/infrastructure-master-plan/update",
        { ...updateData, master_plan_documents: updateData.master_plan_documents.filter((document: IProjectDocument) => document?.doc_id === 0) }
    );
}

export function getInfrastructureMasterPlanDocuments(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IInfrastructureMasterPlan>>(
        "/project-stage-four/infrastructure-master-plan/get",
        { project_id },
        { signal }
    );
}

export function getProjectStage4Phases(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectPhase[]>>(
        "/project-stage-four/project-plan-phases/list",
        { project_id },
        { signal }
    );
}

export function storeOrUpdateProjectStage4Phase(
    updateData: IProjectPhase & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IProjectPhase>>(
        "/project-stage-four/project-plan-phases/save",
        updateData,
        { signal }
    );
}

export function deleteProjectStage4Phase(phase_id: number, project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectPhase>>(
        "/project-stage-four/project-plan-phases/delete",
        { phase_id, project_id },
        { signal }
    );
}

export function getProjectStage4Milestones(project_id: number, phase_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectPhaseMilestone[]>>(
        "/project-stage-four/project-plan-milestones/list",
        { project_id, phase_id },
        { signal }
    );
}

export function storeOrUpdateProjectStage4Milestone(
    updateData: IProjectPhaseMilestone & { project_id: number; phase_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IProjectPhaseMilestone>>(
        "/project-stage-four/project-plan-milestones/save",
        updateData,
        { signal }
    );
}

export function deleteProjectStage4Milestone(milestone_id: number, project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ milestone_id: number }>>(
        "/project-stage-four/project-plan-milestones/delete",
        { milestone_id, project_id },
        { signal }
    );
}

export function getStakeholders(project_id: number | string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IStakeHolder[]>>(
        "/project-stage-four/stakeholders/list",
        { project_id },
        { signal }
    );
}

export function storeOrUpdateStakeholders(
    updateData: IStakeHolder & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IStakeHolder>>(
        "/project-stage-four/stakeholders/save",
        updateData,
        { signal }
    );
}

export function deleteStakeholders(stakeholder_id: number, project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ stakeholder_id: number }>>(
        "/project-stage-four/stakeholders/delete",
        { stakeholder_id, project_id },
        { signal }
    );
}
export const stageFourStakeholderApi: StakeholderApi = {
    list: getStakeholders,
    save: storeOrUpdateStakeholders,
    remove: deleteStakeholders,
};


export function getKeyVariablesSensitivityResults(project_id: number | string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IKeyVariablesSensitivity[]>>(
        "/project-stage-four/key-variables-sensitivity-results/list",
        { project_id },
        { signal }
    );
}

export function storeOrUpdateKeyVariablesSensitivityResult(
    updateData: IKeyVariablesSensitivity & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IKeyVariablesSensitivity>>(
        "/project-stage-four/key-variables-sensitivity-results/save",
        updateData,
        { signal }
    );
}

export function deleteKeyVariablesSensitivityResult(row_id: number, project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ row_id: number }>>(
        "/project-stage-four/key-variables-sensitivity-results/delete",
        { row_id, project_id },
        { signal }
    );
}

export interface updateSummaryOfOptionConsideredPayload
    extends Omit<
        ISummaryOfOptionConsidered,
        "formAction" | "iscompleted"
    > {
    project_id: number;
}

export function updateSummaryOfOptionConsidered(
    updateData: updateSummaryOfOptionConsideredPayload,
    signal?: AbortSignal
) {
    return updateprojectstage4minieditor<ISummaryOfOptionConsidered, updateSummaryOfOptionConsideredPayload>(
        updateData,
        signal
    );
}

export function getSummaryOfOptionConsidered(
    payload: updateSummaryOfOptionConsideredPayload,
    signal?: AbortSignal
) {
    return getprojectstage4minieditor<ISummaryOfOptionConsidered, updateSummaryOfOptionConsideredPayload>(
        payload,
        signal
    );
}

export function getOptionsConsidered(project_id: number | string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IOptionsConsidered[]>>(
        "/project-stage-four/summary-of-options-considered/list",
        { project_id },
        { signal }
    );
}

export function storeOrUpdateSummaryOfOptionsConsidered(payload: IOptionsConsidered & { project_id: number }) {
    return requestLaravel<ApiResponse<IOptionsConsidered>>(
        "/project-stage-four/summary-of-options-considered/update",
        payload
    );
}
